import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  ShoppingCart, 
  User, 
  Menu, 
  X, 
  Coffee, 
  Sun, 
  Moon,
  LogOut,
  MapPin,
  ClipboardList,
  Bell,
  Globe,
  DollarSign
} from 'lucide-react';
import io from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { logout } from '../redux/slices/authSlice';
import { clearCart } from '../redux/slices/cartSlice';
import api, { BASE_URL } from '../services/api';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const { currency, toggleCurrency } = useCurrency();
  const { i18n, t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { items } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  const cartCount = items.reduce((acc, item) => acc + item.quantity, 0);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications/my');
      const unread = res.data.data.filter(n => !n.isRead).length;
      setUnreadCount(unread);
    } catch (err) {
      console.log('Error fetching notifications:', err);
    }
  }, [user]);

  useEffect(() => {
    fetchUnread();
  }, [fetchUnread]);

  useEffect(() => {
    if (!user) return;

    const socket = io(BASE_URL);

    // Join user room
    const roomId = user.id || user._id;
    socket.emit('join', roomId);

    // Listen for notification changes
    const notificationEvents = [
      'newNotification',
      'notificationRead',
      'notificationDeleted',
      'notificationsCleared'
    ];

    notificationEvents.forEach(event => {
      socket.on(event, () => {
        console.log(`[SOCKET] ${event} received, updating unread count...`);
        fetchUnread();
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [user, fetchUnread]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearCart());
    navigate('/');
  };

  const navLinks = [
    { name: t('common.menu', 'Menu'), path: '/products', icon: Coffee },
    { name: t('common.locations', 'Locations'), path: '/locations', icon: MapPin },
    { name: t('common.orders', 'Orders'), path: '/orders', icon: ClipboardList, protected: true },
  ];

  return (
    <header 
      className="sticky top-0 z-50 backdrop-blur-md border-b transition-colors duration-300"
      style={{ 
        backgroundColor: `${theme.card}cc`, 
        borderColor: theme.border 
      }}
    >
      <div className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
            <Coffee size={18} md={24} style={{ color: theme.background }} />
          </div>
          <span className="text-base md:text-xl font-black uppercase tracking-widest hidden sm:block" style={{ color: theme.text }}>
            Optimist
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            (!link.protected || user) && (
              <Link 
                key={link.name} 
                to={link.path}
                className="text-xs md:text-sm font-black uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity"
                style={{ color: theme.text }}
              >
                {link.name}
              </Link>
            )
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Language Switcher */}
          <button 
            onClick={toggleLanguage}
            className="p-1.5 md:p-2 rounded-lg md:rounded-xl transition-colors flex items-center gap-1"
            style={{ backgroundColor: theme.surface }}
            title={t('common.language', 'Language')}
          >
            <Globe size={16} md={20} />
            <span className="text-[10px] font-bold uppercase">{i18n.language === 'en' ? 'AR' : 'EN'}</span>
          </button>

          {/* Currency Switcher */}
          <button 
            onClick={toggleCurrency}
            className="p-1.5 md:p-2 rounded-lg md:rounded-xl transition-colors flex items-center gap-1"
            style={{ backgroundColor: theme.surface }}
            title={t('common.currency', 'Currency')}
          >
            <DollarSign size={16} md={20} />
            <span className="text-[10px] font-bold uppercase">{currency === 'USD' ? 'LBP' : 'USD'}</span>
          </button>

          <button 
            onClick={toggleTheme}
            className="p-1.5 md:p-2 rounded-lg md:rounded-xl transition-colors"
            style={{ backgroundColor: theme.surface }}
          >
            {isDarkMode ? <Sun size={16} md={20} /> : <Moon size={16} md={20} />}
          </button>

          {user && (
            <Link to="/notifications" className="relative p-1.5 md:p-2 rounded-lg md:rounded-xl transition-colors" style={{ backgroundColor: theme.surface }}>
              <Bell size={16} md={20} />
              {unreadCount > 0 && (
                <span 
                  className="absolute -top-1 -end-1 w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center text-[8px] md:text-[10px] font-black"
                  style={{ backgroundColor: theme.primary, color: theme.background }}
                >
                  {unreadCount}
                </span>
              )}
            </Link>
          )}

          <Link to="/cart" className="relative p-1.5 md:p-2 rounded-lg md:rounded-xl transition-colors" style={{ backgroundColor: theme.surface }}>
            <ShoppingCart size={16} md={20} />
            {cartCount > 0 && (
              <span 
                className="absolute -top-1 -end-1 w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center text-[8px] md:text-[10px] font-black"
                style={{ backgroundColor: theme.primary, color: theme.background }}
              >
                {cartCount}
              </span>
            )}
          </Link>

          {user ? (
            <div className="relative group">
              <button 
                className="p-1.5 md:p-2 rounded-lg md:rounded-xl transition-colors flex items-center gap-2"
                style={{ backgroundColor: theme.surface }}
              >
                <User size={16} md={20} />
                <span className="text-[10px] font-black uppercase tracking-widest hidden xl:block">
                  {user.firstName}
                </span>
              </button>
              <div 
                className="absolute end-0 mt-2 w-48 rounded-2xl shadow-xl border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300"
                style={{ backgroundColor: theme.card, borderColor: theme.border }}
              >
                <div className="p-4 flex flex-col gap-2">
                  <Link to="/profile" className="flex items-center gap-2 p-2 rounded-xl hover:bg-opacity-5" style={{ backgroundColor: theme.surface }}>
                    <User size={16} />
                    <span className="text-xs font-bold">Profile</span>
                  </Link>
                  <Link to="/orders" className="flex items-center gap-2 p-2 rounded-xl hover:bg-opacity-5" style={{ backgroundColor: theme.surface }}>
                    <ClipboardList size={16} />
                    <span className="text-xs font-bold">My Orders</span>
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 p-2 rounded-xl hover:bg-opacity-5 text-red-500" 
                    style={{ backgroundColor: theme.surface }}
                  >
                    <LogOut size={16} />
                    <span className="text-xs font-bold">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Link 
              to="/login"
              className="px-4 md:px-6 py-1.5 md:py-2 rounded-lg md:rounded-xl font-black uppercase tracking-widest text-[10px] md:text-xs shadow-lg transition-transform active:scale-95 whitespace-nowrap"
              style={{ backgroundColor: theme.primary, color: theme.background }}
            >
              Sign In
            </Link>
          )}

          {/* Mobile Menu Toggle */}
          <button 
            className="lg:hidden p-1.5 md:p-2 rounded-lg md:rounded-xl"
            style={{ backgroundColor: theme.surface }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={16} md={20} /> : <Menu size={16} md={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden border-t p-4 flex flex-col gap-3" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
          {navLinks.map((link) => (
            (!link.protected || user) && (
              <Link 
                key={link.name} 
                to={link.path}
                className="block text-[10px] md:text-sm font-black uppercase tracking-widest p-3 md:p-4 rounded-xl md:rounded-2xl"
                style={{ backgroundColor: theme.surface, color: theme.text }}
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="flex items-center gap-3">
                  <link.icon size={16} md={18} />
                  <span>{link.name}</span>
                </div>
              </Link>
            )
          ))}
          {user && (
            <>
              <Link 
                to="/profile"
                className="block text-[10px] font-black uppercase tracking-widest p-3 rounded-xl"
                style={{ backgroundColor: theme.surface, color: theme.text }}
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="flex items-center gap-3">
                  <User size={16} />
                  <span>Profile</span>
                </div>
              </Link>
              <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-red-500 font-black uppercase tracking-widest text-[10px]"
                style={{ backgroundColor: theme.surface }}
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
